import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { getUser, updateUser, withUserUpdateRequired } from "../../api/api";
import { ProfileEditField } from "./ProfileEditField";
import UserProfileHeader from "./UserProfileHeader";
import UserProfileInfo from "./UserProfileInfo";
import { UserProfileSidebar } from "./ProfileSidebar";
import { Skeleton } from "../ui/skeleton";
import { FormattedBody } from "../ui/formatted-body";
import { showVerificationWaitNotice } from "../utils";

/**
 * @param {boolean|undefined} reduced
 * @returns {Object}
 */
const fadeUpMotion = (reduced) =>
  reduced
    ? {}
    : {
        initial: { opacity: 0, y: 10 },
        animate: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] },
        },
      };

/**
 * User profile: header, optional mission line, about/sidebar, or edit mode.
 * @param {Object} props
 * @param {number} props.userId - Logged-in user id
 * @param {Object} props.selfUser - Current session user object
 * @param {function} [props.refreshUser] - Refetch session user after self edit
 * @returns {JSX.Element}
 */
const ProfilePage = ({ userId, selfUser, refreshUser }) => {
  const { profileUserId } = useParams();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const reduceMotion = useReducedMotion();

  const targetUserId = parseInt(profileUserId, 10) || userId;
  const isOwnProfile = Number(targetUserId) === Number(userId);
  const setupRequested = searchParams.get("setup") === "1";

  useEffect(() => {
    const fetchUser = async () => {
      if (!targetUserId) {
        setError("No user ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await getUser(userId, targetUserId);

        if (result.error) {
          setError(result.error);
        } else {
          setUser(result.data);
        }
      } catch (err) {
        setError("Failed to load user profile");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [targetUserId, userId]);

  useEffect(() => {
    if (setupRequested && isOwnProfile) {
      setIsEditing(true);
    }
  }, [setupRequested, isOwnProfile]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = async (editForm) => {
    editForm.links = editForm.links.map((link) => ({
      label: link.label,
      url: link.url,
    }));
    const result = await updateUser(targetUserId, withUserUpdateRequired(user, editForm));
    if (result.error) {
      alert("Failed to update profile: " + result.error);
      throw new Error(result.error);
    }
    setUser(result.data);
    setIsEditing(false);
    if (targetUserId === userId && refreshUser) {
      await refreshUser();
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-ui-background">
        <div className="w-full max-w-md space-y-4 px-6">
          <div className="flex flex-col items-center gap-2">
            <Skeleton className="h-20 w-20 rounded-full" />
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-ui-background p-6">
        <div className="max-w-md rounded-xl border border-ui-border bg-ui-card p-8 text-center shadow-profile-md">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-ui-muted text-error">
            <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-text-primary">Error loading profile</h2>
          <p className="mt-2 text-sm text-text-secondary">{error}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-ui-background p-6">
        <div className="max-w-md rounded-xl border border-ui-border bg-ui-card p-8 text-center shadow-profile-md">
          <h2 className="text-xl font-semibold text-text-primary">User not found</h2>
          <p className="mt-2 text-sm text-text-secondary">The requested profile could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ui-background pb-8">
      <UserProfileHeader
        user={user}
        userId={userId}
        selfUser={selfUser}
        targetUserId={targetUserId}
        isEditing={isEditing}
        onEdit={handleEditClick}
        reduceMotion={reduceMotion}
      />
      {showVerificationWaitNotice(user, isOwnProfile) ? (
        <div className="mx-auto w-full max-w-7xl px-4 pt-6">
          <div className="rounded-lg border border-accent bg-accent/10 px-4 py-3" role="status">
            <p className="text-sm font-semibold text-accent">Your account is waiting for verification</p>
            <p className="mt-1 text-sm text-text-primary">
              Once verified, you will have access to the full features of Assembly Platform.
            </p>
          </div>
        </div>
      ) : null}
      {user.mission_statement && !isEditing && (
        <motion.div
          className="bg-ui-muted/40 px-4 pt-6"
          {...fadeUpMotion(reduceMotion)}
        >
          <FormattedBody
            as="p"
            className="mx-auto max-w-3xl text-center text-base italic text-text-secondary"
          >
            {user.mission_statement}
          </FormattedBody>
        </motion.div>
      )}

      <motion.main
        className="mx-auto w-full max-w-7xl px-4 py-6"
        {...(reduceMotion
          ? {}
          : {
              initial: { opacity: 0, y: 10 },
              animate: {
                opacity: 1,
                y: 0,
                transition: { delay: 0.05, duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] },
              },
            })}
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2">
            {isEditing ? (
              <ProfileEditField user={user} onSave={handleSave} onCancel={handleCancel} />
            ) : (
              <UserProfileInfo
                user={user}
                userId={userId}
                selfUser={selfUser}
                reduceMotion={reduceMotion}
              />
            )}
          </div>
          <div className="lg:col-span-1">
            <UserProfileSidebar
              user={user}
              isEditing={isEditing}
              reduceMotion={reduceMotion}
            />
          </div>
        </div>
      </motion.main>
    </div>
  );
};

export default ProfilePage;
